import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  ArrowLeft, Shield, CheckCircle, AlertCircle, 
  Clock, Mail, Loader2, RefreshCw
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import MobileBottomNav from '../../components/layout/MobileBottomNav';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import { AlertModal } from '../../components/common/Modal'; // Import AlertModal
import { verifyWithdrawalOTP, resendWithdrawalOTP } from '../../services/api';

const WithdrawOtpPage = () => {
  const { user: authUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [resending, setResending] = useState(false);
  const [timer, setTimer] = useState(300);
  const [canResend, setCanResend] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isAllDigitsFilled, setIsAllDigitsFilled] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const submissionRef = useRef(false);

  // Single ref array for OTP inputs
  const otpInputRefs = useRef([]);

  // Get data from navigation state
  const { transactionRef, amount, payoutMethod } = location.state || {};

  useEffect(() => {
    // Check if mobile on mount and resize
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (!authUser) {
      navigate('/login');
      return;
    }

    if (!transactionRef || !amount) {
      navigate('/wallet/withdraw');
      return;
    }

    // Start countdown timer
    const interval = setInterval(() => {
      setTimer(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          setCanResend(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [authUser, navigate, transactionRef, amount]);

  useEffect(() => {
    // Check if all digits are filled
    const filled = otp.every(digit => digit !== '');
    setIsAllDigitsFilled(filled);
    
    // Auto-focus first input on load or when all digits are cleared
    if (otpInputRefs.current[0] && otp.every(digit => digit === '')) {
      otpInputRefs.current[0].focus();
    }
  }, [otp, isMobile]);

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '₦0.00';
    return `₦${parseFloat(amount).toLocaleString('en-NG', { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    })}`;
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleOtpChange = useCallback((index, value) => {
    // Allow only numbers
    const numericValue = value.replace(/[^0-9]/g, '');
    
    // If user pastes multiple digits, handle them
    if (numericValue.length > 1) {
      const digits = numericValue.split('').slice(0, 6);
      const newOtp = [...otp];
      digits.forEach((digit, idx) => {
        if (index + idx < 6) {
          newOtp[index + idx] = digit;
        }
      });
      setOtp(newOtp);
      setError('');
      
      // Focus the next empty input or last input
      const nextEmptyIndex = newOtp.findIndex((digit, idx) => 
        idx >= index && digit === ''
      );
      const focusIndex = nextEmptyIndex === -1 ? 5 : nextEmptyIndex;
      setTimeout(() => {
        if (otpInputRefs.current[focusIndex]) {
          otpInputRefs.current[focusIndex].focus();
        }
      }, 10);
      return;
    }
    
    const newOtp = [...otp];
    newOtp[index] = numericValue;
    setOtp(newOtp);
    setError('');

    // Auto-focus next input if current input has a value
    if (numericValue && index < 5) {
      setTimeout(() => {
        if (otpInputRefs.current[index + 1]) {
          otpInputRefs.current[index + 1].focus();
          otpInputRefs.current[index + 1].select();
        }
      }, 10);
    }
  }, [otp]);

  const handleKeyDown = useCallback((index, e) => {
    // Handle backspace - clear current and move to previous
    if (e.key === 'Backspace') {
      e.preventDefault();
      
      const newOtp = [...otp];
      if (newOtp[index]) {
        // Clear current input
        newOtp[index] = '';
        setOtp(newOtp);
      } else if (index > 0) {
        // Move to previous and clear it
        newOtp[index - 1] = '';
        setOtp(newOtp);
        setTimeout(() => {
          if (otpInputRefs.current[index - 1]) {
            otpInputRefs.current[index - 1].focus();
            otpInputRefs.current[index - 1].select();
          }
        }, 10);
      }
      return;
    }
    
    // Handle left arrow
    if (e.key === 'ArrowLeft' && index > 0) {
      e.preventDefault();
      setTimeout(() => {
        if (otpInputRefs.current[index - 1]) {
          otpInputRefs.current[index - 1].focus();
          otpInputRefs.current[index - 1].select();
        }
      }, 10);
    }
    
    // Handle right arrow
    if (e.key === 'ArrowRight' && index < 5) {
      e.preventDefault();
      setTimeout(() => {
        if (otpInputRefs.current[index + 1]) {
          otpInputRefs.current[index + 1].focus();
          otpInputRefs.current[index + 1].select();
        }
      }, 10);
    }
  }, [otp]);

  const handlePaste = useCallback((e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text');
    const digits = pastedData.replace(/[^0-9]/g, '').split('').slice(0, 6);
    
    const newOtp = [...otp];
    digits.forEach((digit, index) => {
      if (index < 6) newOtp[index] = digit;
    });
    
    setOtp(newOtp);
    setError('');
    
    // Focus last filled input or the last input
    const focusIndex = Math.min(digits.length, 5);
    setTimeout(() => {
      if (otpInputRefs.current[focusIndex]) {
        otpInputRefs.current[focusIndex].focus();
        otpInputRefs.current[focusIndex].select();
      }
    }, 10);
  }, [otp]);

  const handleSubmit = useCallback(async () => {
    const otpString = otp.join('');
    
    // ✅ PREVENT MULTIPLE SUBMISSIONS
    if (submissionRef.current) {
        // console.log('🚫 Blocked: Already submitting');
        return;
    }
    
    if (isSubmitting || processing) {
        // console.log('🚫 Blocked: isSubmitting or processing');
        return;
    }
    
    // Validate OTP
    if (otpString.length !== 6) {
        setError('Please enter a valid 6-digit OTP');
        
        // Find first empty input and focus it
        const firstEmptyIndex = otp.findIndex(digit => digit === '');
        setTimeout(() => {
            const focusIndex = firstEmptyIndex === -1 ? 0 : firstEmptyIndex;
            if (otpInputRefs.current[focusIndex]) {
                otpInputRefs.current[focusIndex].focus();
            }
        }, 10);
        return;
    }

    // Check if all are digits
    if (!/^\d{6}$/.test(otpString)) {
        setError('OTP must contain only numbers (0-9)');
        return;
    }

    try {
        // ✅ MARK AS SUBMITTING
        submissionRef.current = true;
        setIsSubmitting(true);
        setProcessing(true);
        setError('');

        // console.log('🟡 Submitting OTP:', otpString, 'at', new Date().toISOString());

        const result = await verifyWithdrawalOTP(
            transactionRef,
            otpString,
            authUser.user_id
        );

        // console.log('🟢 OTP API Response:', result);

        if (result.success) {
            // Show success modal instead of inline message
            setShowSuccessModal(true);
            
            // ✅ CLEAR OTP IMMEDIATELY
            setOtp(['', '', '', '', '', '']);
        } else {
            if (result.error.includes('Invalid') || result.error.includes('expired')) {
                throw new Error('INVALID_OTP: ' + result.error);
            } else if (result.error.includes('Bank transfer failed')) {
                throw new Error('WITHDRAWAL_FAILED: ' + result.error);
            } else {
                throw new Error(result.error || 'OTP verification failed');
            }
        }
    } catch (err) {
        // console.error('❌ OTP submission error:', err);
        
        if (err.message.includes('INVALID_OTP') || err.message.includes('Invalid OTP') || err.message.includes('expired')) {
            setError('Invalid or expired OTP code. Please try again.');
        } else if (err.message.includes('WITHDRAWAL_FAILED') || err.message.includes('Bank transfer failed')) {
            setError('OTP was verified but the bank transfer failed. Please try again.');
        } else {
            setError(err.message || 'OTP verification failed. Please try again.');
        }
        
        // Clear OTP on error and focus first input
        setOtp(['', '', '', '', '', '']);
        setTimeout(() => {
            if (otpInputRefs.current[0]) {
                otpInputRefs.current[0].focus();
            }
        }, 10);
    } finally {
        // ✅ RESET SUBMISSION STATE WITH DELAY
        setTimeout(() => {
            submissionRef.current = false;
            setIsSubmitting(false);
            setProcessing(false);
        }, 1000); // 1 second cooldown
    }
  }, [otp, transactionRef, authUser, isSubmitting, processing]);

  const handleResendOtp = async () => {
    try {
      setResending(true);
      
      const result = await resendWithdrawalOTP(transactionRef, authUser.user_id);
      
      if (result.success) {
        setError('');
        setCanResend(false);
        setTimer(300);
        
        // Restart countdown
        const interval = setInterval(() => {
          setTimer(prev => {
            if (prev <= 1) {
              clearInterval(interval);
              setCanResend(true);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
        
        // Clear current OTP
        setOtp(['', '', '', '', '', '']);
        setTimeout(() => {
          if (otpInputRefs.current[0]) {
            otpInputRefs.current[0].focus();
          }
        }, 10);
      } else {
        setError('Failed to resend OTP. Please try again.');
      }
    } catch (err) {
      // console.error('Resend OTP error:', err);
      setError('Failed to resend OTP. Please try again.');
    } finally {
      setResending(false);
    }
  };

  const handleBack = () => {
    navigate('/wallet/withdraw');
  };

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    navigate('/wallet', {
      state: {
        message: `Withdrawal of ${formatCurrency(amount)} initiated successfully.`,
        success: true
      }
    });
  };

  // Memoize the OTP inputs to prevent remounting
  const otpInputs = useMemo(() => 
    otp.map((digit, index) => (
      <input
        key={`otp-${index}`}
        ref={el => otpInputRefs.current[index] = el}
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        maxLength="6"
        value={digit}
        onChange={(e) => handleOtpChange(index, e.target.value)}
        onKeyDown={(e) => handleKeyDown(index, e)}
        onPaste={index === 0 ? handlePaste : undefined}
        className={`
          ${isMobile ? 'w-12 h-12 text-xl rounded-lg' : 'w-16 h-16 text-2xl rounded-xl'}
          font-bold text-center border-2 
          ${error && error.includes('OTP') ? 'border-red-500' : 'border-gray-300'}
          focus:outline-none focus:border-[#ff660e] focus:ring-2 focus:ring-orange-100
          disabled:opacity-50 disabled:cursor-not-allowed
          transition-colors
        `}
        disabled={processing}
        autoComplete="one-time-code"
        aria-label={`OTP digit ${index + 1}`}
      />
    )), [otp, processing, isMobile, error, handleOtpChange, handleKeyDown, handlePaste]
  );

  if (isMobile) {
    return (
      <>
        <AlertModal
          isOpen={showSuccessModal}
          onClose={handleSuccessModalClose}
          title="Withdrawal Successful!"
          message={`Your withdrawal of ${formatCurrency(amount)} is successful.\n\nFunds will be transferred to your bank account within 5 minutes.`}
          buttonText="Back to Wallet"
          type="success"
          onButtonClick={handleSuccessModalClose}
        />
        
        <div className="min-h-screen bg-gray-50 pb-20">
          {/* Mobile Header */}
          <header className="bg-white shadow-sm sticky top-0 z-50">
            <div className="px-4 py-3">
              <div className="flex items-center justify-between">
                <button
                  onClick={handleBack}
                  className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-100"
                >
                  <ArrowLeft className="w-5 h-5 text-black" />
                </button>
                
                <h1 className="text-lg font-bold text-black">Verify Withdrawal</h1>
                
                <div className="w-10"></div>
              </div>
            </div>
          </header>

          <main className="px-4 py-4">
            {/* Amount Card */}
            <div className="bg-white rounded-xl shadow-sm p-4 mb-4">
              <div className="text-center">
                <p className="text-sm text-gray-600 mb-2">Amount to be Debited</p>
                <p className="text-2xl font-bold text-black">{formatCurrency(amount)}</p>
                
                {payoutMethod && (
                  <div className="mt-3 pt-3 border-t">
                    <p className="text-sm text-gray-600 mb-1">Bank Account:</p>
                    <p className="font-medium text-black">
                      {payoutMethod.details.bank_name} ••••{payoutMethod.details.account_number?.slice(-4)}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Timer */}
            <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-yellow-600" />
                  <span className="font-medium text-yellow-800">
                    Expires in: {formatTime(timer)}
                  </span>
                </div>
                {timer === 0 && (
                  <span className="text-sm font-semibold text-red-600">EXPIRED</span>
                )}
              </div>
            </div>

            {/* OTP Input */}
            <div className="bg-white rounded-xl shadow-sm p-4 mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-4 text-center">
                Enter 6-digit verification code
              </label>
              
              <div className="flex justify-center gap-2 mb-4">
                {otpInputs}
              </div>
              
              <div className="text-center">
                <p className="text-sm text-gray-600 flex items-center justify-center gap-1">
                  <Mail className="w-4 h-4" />
                  <span>OTP sent to: {authUser?.email}</span>
                </p>
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-red-600" />
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              </div>
            )}

            {/* Verify Button */}
            <button
              onClick={handleSubmit}
              disabled={processing || otp.join('').length !== 6}
              className="w-full bg-gradient-to-r from-[#ff660e] to-orange-500 text-white py-3.5 rounded-lg font-bold disabled:opacity-50 mb-4"
            >
              {processing ? 'Verifying...' : 'Verify & Complete Withdrawal'}
            </button>

            {/* Resend OTP */}
            <div className="text-center mb-4">
              <p className="text-sm text-gray-600 mb-2">
                Didn't receive the code?
              </p>
              <button
                onClick={handleResendOtp}
                disabled={!canResend || resending || processing}
                className="text-sm text-[#ff660e] font-medium flex items-center justify-center gap-1 mx-auto disabled:opacity-50"
              >
                {resending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4" />
                    Resend OTP {!canResend && `(${formatTime(timer)})`}
                  </>
                )}
              </button>
            </div>

            {/* Security Info */}
            <div className="bg-white rounded-xl shadow-sm p-4">
              <div className="flex items-start gap-2 mb-3">
                <Shield className="w-5 h-5 text-green-600 mt-0.5" />
                <h3 className="font-bold text-black">Security Information</h3>
              </div>
              
              <div className="space-y-1 text-sm text-gray-600">
                <p>• OTP valid for 5 minutes only</p>
                <p>• Never share your OTP with anyone</p>
                <p>• Check spam folder if missing</p>
                <p>• Funds transferred within 24 hours</p>
              </div>
            </div>
          </main>

          {/* Mobile Bottom Navigation */}
          <MobileBottomNav />
        </div>
      </>
    );
  }

  // Desktop View
  return (
    <>
      <AlertModal
        isOpen={showSuccessModal}
        onClose={handleSuccessModalClose}
        title="Withdrawal Successful!"
        message={`Your withdrawal of ${formatCurrency(amount)} has been initiated successfully.\n\nFunds will be transferred to your bank account within 24 hours.`}
        buttonText="Back to Wallet"
        type="success"
        onButtonClick={handleSuccessModalClose}
      />
      
      <div className="min-h-screen bg-gray-50">
        {/* Desktop Header */}
        <Header />
        
        <main className="max-w-7xl mx-auto px-6 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - OTP Verification */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-2xl shadow-lg p-6">
                {/* Header */}
                <div className="flex items-center gap-4 mb-6">
                  <button
                    onClick={handleBack}
                    className="p-2 hover:bg-gray-100 rounded-lg"
                  >
                    <ArrowLeft className="w-5 h-5 text-black" />
                  </button>
                  <div>
                    <h1 className="text-2xl font-bold text-black">Verify Withdrawal</h1>
                    <p className="text-gray-600">Enter OTP sent to your email</p>
                  </div>
                </div>

                {/* Transaction Details */}
                <div className="mb-6 p-5 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl">
                  <div className="flex flex-col items-center">
                    <p className="text-sm text-gray-600 mb-2">Amount to be Debited</p>
                    <p className="text-3xl font-bold text-black mb-4">{formatCurrency(amount)}</p>
                    
                    {payoutMethod && (
                      <div className="text-center">
                        <p className="text-sm text-gray-600 mb-1">Bank Account</p>
                        <p className="font-medium text-black">
                          {payoutMethod.details.bank_name} ••••{payoutMethod.details.account_number?.slice(-4)}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Timer */}
                <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-xl">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Clock className="w-5 h-5 text-yellow-600" />
                      <div>
                        <p className="font-medium text-yellow-800">OTP Expires in {formatTime(timer)}</p>
                        <p className="text-sm text-yellow-700">
                          Enter the 6-digit code before it expires
                        </p>
                      </div>
                    </div>
                    {timer === 0 && (
                      <span className="font-semibold text-red-600">EXPIRED</span>
                    )}
                  </div>
                </div>

                {/* OTP Input */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-4 text-center">
                    Enter 6-digit verification code
                  </label>
                  
                  <div className="flex justify-center gap-3 mb-6">
                    {otpInputs}
                  </div>
                  
                  <div className="text-center">
                    <p className="text-sm text-gray-600 flex items-center justify-center gap-2">
                      <Mail className="w-4 h-4" />
                      <span>OTP sent to: {authUser?.email}</span>
                    </p>
                  </div>
                </div>

                {/* Error Message */}
                {error && (
                  <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
                    <div className="flex items-center gap-3">
                      <AlertCircle className="w-5 h-5 text-red-600" />
                      <p className="text-red-700">{error}</p>
                    </div>
                  </div>
                )}

                {/* Verify Button */}
                <button
                  onClick={handleSubmit}
                  disabled={processing || otp.join('').length !== 6}
                  className="w-full bg-gradient-to-r from-[#ff660e] to-orange-500 text-white py-4 rounded-xl font-bold text-lg hover:from-[#ff5500] hover:to-orange-600 disabled:opacity-50 transition-colors"
                >
                  {processing ? (
                    <div className="flex items-center justify-center gap-2">
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Verifying...
                    </div>
                  ) : (
                    'Verify & Complete Withdrawal'
                  )}
                </button>

                {/* Resend OTP */}
                <div className="text-center mt-6">
                  <p className="text-sm text-gray-600 mb-2">
                    Didn't receive the code?
                  </p>
                  <button
                    onClick={handleResendOtp}
                    disabled={!canResend || resending || processing}
                    className="text-sm text-[#ff660e] hover:text-[#ff5500] font-medium flex items-center justify-center gap-2 mx-auto disabled:opacity-50"
                  >
                    {resending ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="w-4 h-4" />
                        Resend OTP {!canResend && `(${formatTime(timer)})`}
                      </>
                    )}
                  </button>
                </div>

                {/* Cancel Button */}
                <div className="text-center mt-4">
                  <button
                    onClick={handleBack}
                    className="text-sm text-gray-600 hover:text-gray-800 hover:underline"
                    disabled={processing}
                  >
                    Cancel Withdrawal
                  </button>
                </div>
              </div>
            </div>

            {/* Right Column - Info */}
            <div className="space-y-6">
              {/* Security Info */}
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Shield className="w-5 h-5 text-green-600" />
                  <h3 className="font-bold text-black">Security Information</h3>
                </div>
                
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                    <p className="text-sm text-gray-600">OTP is valid for 5 minutes only</p>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                    <p className="text-sm text-gray-600">Never share your OTP with anyone</p>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                    <p className="text-sm text-gray-600">Check your spam folder if missing</p>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                    <p className="text-sm text-gray-600">Funds transferred within 24 hours</p>
                  </div>
                </div>
              </div>

              {/* Help */}
              <div className="bg-blue-50 border border-blue-200 rounded-2xl p-6">
                <h4 className="font-bold text-black mb-3">Need help with OTP?</h4>
                
                <div className="space-y-2 text-sm text-gray-700">
                  <p>• Check email: {authUser?.email}</p>
                  <p>• Look in spam or junk folder</p>
                  <p>• Wait a few minutes and resend</p>
                  <p>• Ensure email inbox is not full</p>
                </div>
              </div>

              {/* Note */}
              <div className="bg-orange-50 border border-orange-200 rounded-2xl p-6">
                <h4 className="font-bold text-black mb-3">Important Note</h4>
                <p className="text-sm text-gray-700">
                  The amount shown above ({formatCurrency(amount)}) is the exact amount that will be debited from your wallet.
                </p>
              </div>
            </div>
          </div>
        </main>
        
        {/* Desktop Footer */}
        <Footer />
      </div>
    </>
  );
};

export default WithdrawOtpPage;